import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ResponsiveAppBar from "./AppBar";
import { API_BASE_URL } from "../api/config";

const BASE_URL = `${API_BASE_URL}/user`;
const loanTypes = ["vehicle", "gold"];
const loanStatuses = ["pending", "approved", "rejected"];

const UserTable = () => {
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [setRole] = useState(null);

  // Function to Show Google-style Alerts
  const showAlert = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Close Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle User Login
  const handleLogin = async () => {
    if (!userId || !password) {
      showAlert("Please enter User ID and Password.", "error");
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/login-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, password }),
      });

      const data = await response.json();
      if (response.ok) {
        // Store token and role in sessionStorage
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("role", data.role);
        setRole(data.role);

        // Fetch user details
        const userResponse = await fetch(`${BASE_URL}/get_users_by_id/${userId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${data.token}`,
          },
        });

        const userData = await userResponse.json();
        if (userResponse.ok) {
          setUsers([userData]);
          setEditedData({ [userData.userId]: { ...userData } });
          showAlert("Login successful! Data fetched.", "success");
        } else {
          showAlert(userData.message, "error");
        }
      } else {
        showAlert(data.message, "error");
      }
    } catch (error) {
      showAlert("Login error! Check your connection.", "error");
      console.error("Login error:", error);
    }
  };

  // Handle Input Change in Table
  const handleInputChange = (userId, field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], [field]: value },
    }));
  };

  // Handle Save (Updating User Details)
  const handleSave = async (userId) => {
    if (!userId || !editedData[userId]) return;

    const updateData = {};
    const fieldsToUpdate = ["name","userId","email", "loanAmount", "loanType", "loanStatus", "interestRate", "tenureMonths"];

    // Check for fields that changed
    fieldsToUpdate.forEach((field) => {
      const originalValue = users.find(user => user.userId === userId)?.[field];
      const editedValue = editedData[userId][field];
      if (editedValue !== originalValue) {
        updateData[field] = editedValue;
      }
    });

    if (Object.keys(updateData).length === 0) {
      showAlert("No changes detected.", "info");
      return;
    }

    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/get_users_by_id/updateUser/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      const result = await response.json();

      if (response.ok) {
        showAlert("Update successful!", "success");

        // Update user list in state
        const updatedUsers = users.map((user) =>
          user.userId === userId ? { ...user, ...updateData } : user
        );
        setUsers(updatedUsers);
        setEditingUserId(null);
      } else {
        showAlert(result.message || "Update failed!", "error");
      }
    } catch (error) {
      showAlert("Update failed! Try again.", "error");
      console.error("Update error:", error);
    }
  };

  // Handle Delete
  const handleDelete = async (userId) => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/get_users_by_id/${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        showAlert("User deleted successfully!", "success");
        setUsers([]); // Clear the user list after deletion
        setEditedData({});
        setUserId("");
        setPassword("");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("role");
        setRole(null);
      } else {
        showAlert(result.message || "Delete failed!", "error");
      }
    } catch (error) {
      showAlert("Delete failed! Try again.", "error");
      console.error("Delete error:", error);
    }
  };

  return (
    <>
      <ResponsiveAppBar />
      <Box sx={{ maxWidth: "80%", margin: "auto", mt: 4 }}>
        {/* User Login Fields */}
        <TextField
          label="User ID"
          variant="outlined"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          size="small"
          sx={{ mb: 2, mr: 2 }}
        />
        <TextField
          label="Password"
          type="password"
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
          sx={{ mb: 2, mr: 2 }}
        />
        <Button variant="contained" onClick={handleLogin}>
          Login & Fetch User
        </Button>

        {/* User Details Table */}
        {users.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "white" }}>
                  <TableCell>Name</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Loan Amount</TableCell>
                  <TableCell>Interest Rate</TableCell>
                  <TableCell>Tenure (Months)</TableCell>
                  <TableCell>Loan Type</TableCell>
                  <TableCell>Loan Status</TableCell>
                  <TableCell>Actions</TableCell>

                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.userId}>
                    {["name","userId", "email", "loanAmount", "interestRate", "tenureMonths"].map((field) => (
                      <TableCell key={field}>
                        {editingUserId === user.userId ? (
                          <TextField
                            value={editedData[user.userId]?.[field] ?? user[field]}
                            onChange={(e) => handleInputChange(user.userId, field, e.target.value)}
                            size="small"
                          />
                        ) : (
                          user[field]
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      {editingUserId === user.userId ? (
                        <Select
                          value={editedData[user.userId]?.loanType ?? user.loanType}
                          onChange={(e) => handleInputChange(user.userId, "loanType", e.target.value)}
                          size="small"
                        >
                          {loanTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        user.loanType
                      )}
                    </TableCell>
                    <TableCell>
                      {editingUserId === user.userId ? (
                        <Select
                          value={editedData[user.userId]?.loanStatus ?? user.loanStatus}
                          onChange={(e) => handleInputChange(user.userId, "loanStatus", e.target.value)}
                          size="small"
                        >
                          {loanStatuses.map((status) => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        user.loanStatus
                      )}
                    </TableCell>
                    <TableCell>
                        <>
                          {editingUserId === user.userId ? (
                            <Button variant="contained" onClick={() => handleSave(user.userId)}>
                              Save
                            </Button>
                          ) : (
                            <Button variant="outlined" onClick={() => setEditingUserId(user.userId)}>
                              Edit
                            </Button>
                          )}
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(user.userId)}
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Google-like Snackbar Alert */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ display: "flex", alignItems: "center" }}
            action={
              <IconButton size="small" color="inherit" onClick={handleCloseSnackbar}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default UserTable;